import { HttpContext } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  inject, signal, DestroyRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { ReuseTabComponent, ReuseTabService } from '@delon/abc/reuse-tab';
import { ALLOW_ANONYMOUS, DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import { MenuService, SettingsService, User, _HttpClient } from '@delon/theme';
import { LayoutDefaultOptions } from '@delon/theme/layout-default';
import { Store } from '@ngrx/store';
import { NzSafeAny } from 'ng-zorro-antd/core/types';
import { filter, Subscription } from 'rxjs';
import { toggleLockAction } from 'src/app/actions/app';
import { AuthService } from 'src/app/core/util/auth.service';
import { LicenseService } from 'src/app/shared/services/license.service';
import { NotificationService } from 'src/app/shared/services/notification.service';

import { QbotWidgetService } from '../../routes/ai/_providers/qbot-widget.service';

@Component({
  selector: 'layout-basic',
  styleUrls: ['./basic.component.less'],
  templateUrl: './basic.component.html',
  standalone: false
})
export class LayoutBasicComponent implements AfterViewInit, OnDestroy {
  public menuSrv = inject(MenuService);
  private receiveMsg$: Subscription | undefined;
  private reuseTabChangeSubscription?: Subscription;
  private routerEventsSubscription?: Subscription;
  interval$: NzSafeAny;
  count = 0;
  progress = 0;
  options: LayoutDefaultOptions = {
    logoExpanded: ``,
    logoCollapsed: `https://cdn.qne.cloud/qca/assets/img/logo-color.webp`
  };
  searchToggleStatus = false;
  showSettingDrawer = false; //!environment.production;
  licenseText = '';
  countryCode = 'PH';
  isSmallScreen = false; //window.innerWidth < 736, and use to assign class to search button in header

  get user(): User {
    return this.settings.user;
  }

  get company() {
    let company = this.settings.user['company'] ?? { isSample: true };
    return company;
  }

  get country() {
    return this.settings.user['company']?.country;
  }

  get companyName() {
    // if (this.countryCode === 'MY') {
    //   return 'QNE OPTIMUM SDN. BHD.';
    // }
    // return 'QNE SOFTWARE SDN. BHD.';
    return 'QNE SOFTWARE MALAYSIA SDN. BHD.';
  }

  get QNELink() {
    if (this.countryCode === 'PH') {
      return 'https://qne.com.ph/';
    }
    return 'https://www.qne.com.my/';
  }

  get academyLink() {
    if (this.country === 'PH') return 'https://academy.qne.cloud';

    return 'https://academy.qne.cloud/courses/qcamy';
  }

  get websiteLink() {
    if (this.country === 'PH') return 'https://qne.cloud/ph/';

    return 'https://qne.cloud/my/';
  }

  get youtubeLink() {
    if (this.country === 'PH') return 'https://www.youtube.com/@QNESoftwarePH';

    return 'https://www.youtube.com/@QnESoftwareChannel';
  }

  destroyRef = inject(DestroyRef);
  showPremiumFeatureModal = signal<boolean>(true);
  resetPremiumFeatureModal() {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.showPremiumFeatureModal.set(true);
      });
  }

  qbotWidgetService = inject(QbotWidgetService);
  constructor(
    private injector: Injector,
    private settings: SettingsService,
    private router: Router,
    private http: _HttpClient,
    private authService: AuthService,
    @Inject(DA_SERVICE_TOKEN) private tokenService: ITokenService,
    private notifySrv: NotificationService,
    private reuseTabService: ReuseTabService,
    private store: Store<{ app: { isLocked: boolean } }>
  ) {
    this.resetPremiumFeatureModal();
    if (window.name === 'qi') {
      this.options.logoExpanded = `https://cdn.qne.cloud/qca/assets/img/qi/logo-transparent-yellow-blue.png`;
    } else {
      this.options.logoExpanded = `https://cdn.qne.cloud/qca/assets/img/qca/logo-transparent-yellow-blue-512w.webp`;
    }

    if (this.company.bannerLogo)
      this.options.logoExpanded = this.company.bannerLogo;
    if (this.company.squareLogo)
      this.options.logoCollapsed = this.company.squareLogo;

    // retrieve server build version
    this.http.get('api/app/BuildVer?_silent=true').subscribe(res => {
      this._build = res;
    });

    if (this.company.isSample) {
      this.progress = this.company.initProgress;
    }

    this.reuseTabService.keepingScroll = true;

    // Persist and restore query parameters per reuse tab
    const tabQueryStore = new Map<string, string>();
    (this as any)._tabQueryStore = tabQueryStore;
    (window as any).__tabQueryStore = tabQueryStore;

    // Allow other components to request clearing a specific path's saved query
    window.addEventListener('qpa-clear', (ev: any) => {
      const path = ev?.detail?.path as string | undefined;
      if (path) tabQueryStore.delete(path);
    });
    let restoringQuery = false;

    const tryRestoreQuery = () => {
      try {
        const currentPath = this.router.url.split('?')[0];
        const savedQuery = tabQueryStore.get(this.reuseTabService.curUrl);
        const hasQuery = this.router.url.includes('?');
        if (
          !hasQuery &&
          savedQuery &&
          this.reuseTabService.curUrl === currentPath &&
          !restoringQuery
        ) {
          restoringQuery = true;
          this.router
            .navigateByUrl(`${currentPath}?${savedQuery}`)
            .finally(() => (restoringQuery = false));
        }
      } catch (error) {
        // Silently ignore errors if injector is destroyed (component may be destroyed)
        if (
          error instanceof Error &&
          error.message.includes('Injector has already been destroyed')
        ) {
          return;
        }
        // Re-throw other errors
        throw error;
      }
    };

    // Save query params on every navigation end
    this.routerEventsSubscription = this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        const [path, query] = this.router.url.split('?');
        if (query && query.length) {
          tabQueryStore.set(path, query);
        }
        // When navigating to a tab URL without query, try restore
        tryRestoreQuery();
      });

    // When switching tabs, try restore the tab's saved query
    this.reuseTabChangeSubscription = this.reuseTabService.change
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => tryRestoreQuery());
  }

  _build: string | null = null;
  // replaced by birSystemVersion
  get build() {
    if (this.company?.birSystemVersion) return this.company.birSystemVersion;
    if (this._build) return this._build;
    return '0.0.0.0';
  }

  get buildDisplay() {
    if (
      this.company?.birSystemVersion &&
      this.company.birSystemVersion.trim() !== ''
    ) {
      return 'Build@' + this.company.birSystemVersion;
    }
    return 'QNE AI Cloud Accounting v1.0.0';
  }

  get currentYear() {
    return new Date().getFullYear();
  }

  get copyrightYear() {
    return `2022-${this.currentYear}`;
  }

  ngAfterViewInit(): void {
    this.loadLicenseText();
    this.getScreenSize();
    this.loadCountry();
  }

  ngOnDestroy(): void {
    if (this.receiveMsg$) this.receiveMsg$.unsubscribe();
    if (this.reuseTabChangeSubscription) {
      this.reuseTabChangeSubscription.unsubscribe();
    }
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
  }

  loadCountry() {
    this.http
      .get(
        'https://ipinfo.io/json',
        {},
        {
          context: new HttpContext().set(ALLOW_ANONYMOUS, true)
        }
      )
      .subscribe({
        next: (res: any) => {
          this.countryCode = res?.country;
        },
        error: () => (this.countryCode = 'PH')
      });
  }

  get isCollapsed() {
    return this.settings.layout.collapsed;
  }

  get licSrv() {
    return this.injector.get(LicenseService);
  }

  showLicense() {
    if (this.licSrv && this.company.licensed) {
      this.licSrv.modalOpen.licenseInfo = true;
      this.licSrv.show(this.company.tenantId, this.company.ownerId);
    }
  }

  navigateToPlan() {
    const url = `/account/subscription/subscribe/${this.company['tenantId']}`;
    this.router.navigateByUrl(url);
  }

  gotoDashboard() {
    clearInterval(this.interval$);
  }

  loadLicenseText() {
    this.http.get('api/License/LicenseDisplay').subscribe({
      next: (data: any) => {
        this.licenseText = data.toString();
      }
    });
  }

  logout(): void {
    this.authService.logout();
  }

  addTab(reuseTabComponent: ReuseTabComponent, event: any) {
    reuseTabComponent.activate(event);
  }

  closeTab(event: any) {
    // When a tab is closed, remove its saved query so it doesn't persist
    const path =
      typeof event?.url === 'string'
        ? event.url.split('?')[0]
        : this.router.url.split('?')[0];
    // Using a symbol placed on the instance in ctor for the store
    const store: Map<string, string> | undefined = (this as any)._tabQueryStore;
    if (store) {
      store.delete(path);
    }
  }

  getScreenSize() {
    if (window.innerWidth < 736) this.isSmallScreen = true;
    else {
      this.isSmallScreen = false;
    }
  }
  lockApp() {
    this.router.navigateByUrl('/passport/lock');
    this.store.dispatch(toggleLockAction());
  }
}
